import { HeadersFunction } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import { GetContentPageDocument, GetHelpCategoriesDocument, GetTopHelpArticlesDocument } from '@theprintbar/graphql';
import { runSudoGraphql } from '@theprintbar/keystone/context';
import { cachedJsonDay } from '@theprintbar/server';
import { WebRoutes, invariantNotFound } from '@theprintbar/shared';
import { BannerHeader, Container, LinkTiles, SplitCTA, TitleSection } from '@theprintbar/ui';
import { FlipTile } from '~/components/FlipTiles';
import { contentMeta } from '~/utils/contentMeta';

export const meta = contentMeta;

export const headers: HeadersFunction = ({ loaderHeaders }) => ({
	'Cache-Control': loaderHeaders.get('Cache-Control') || '',
});

export const loader = async () => {
	const [{ contentPage }, { helpCategories }, { helpArticles }] = await Promise.all([
		runSudoGraphql(GetContentPageDocument, { where: { slug: 'help' } }),
		runSudoGraphql(GetHelpCategoriesDocument),
		runSudoGraphql(GetTopHelpArticlesDocument),
	]);

	invariantNotFound(contentPage, 'No content found');
	invariantNotFound(helpCategories, 'No help categories found');
	invariantNotFound(helpArticles, 'No help articles found');

	return cachedJsonDay({
		contentPage,
		helpCategories,
	});
};

export default function () {
	const { contentPage, helpCategories } = useLoaderData<typeof loader>();

	return (
		<div>
			<h1 className="hidden">{contentPage.headingTag || contentPage.metaTitle}</h1>
			<BannerHeader cta={{ structure: contentPage.heroBanner.structure?.[0] }} />
			<Container className="my-10 lg:my-20">
				{/* First 3 articles */}
				<div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
					{helpCategories.slice(0, 3).map((article, _index) => (
						<FlipTile
							key={_index}
							tile={article.heroBanner?.structure?.[0]}
							image={article.heroBanner.structure?.[0]?.image}
							link={{ to: article.slug, text: '', isPrimary: false }}
						/>
					))}
				</div>
			</Container>

			<Container className="mb-10 lg:mb-20">
				<LinkTiles
					hasContainer
					imageClassName="border-none"
					content={{
						linkTiles: {
							structure: helpCategories.slice(3).map(({ slug, heroBanner }) => ({
								to: WebRoutes.HelpCategoryPage(slug),
								text: heroBanner.structure?.[0]?.title,
								promoImage: heroBanner.structure?.[0]?.image,
							})),
						},
					}}
				/>
			</Container>

			{/* Simple Dual CTA */}
			<TitleSection content={contentPage.splitCTAContainer}>
				<SplitCTA content={contentPage} />
			</TitleSection>
		</div>
	);
}
